import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, FileText, Mail, ExternalLink } from 'lucide-react';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { CollapsibleSection } from '../components/static/CollapsibleSection';
import { termsContent } from '../data/termsContent';
import { SectionHeading } from '../components/shared/SectionHeading';

export const TermsPage: React.FC = () => {
  const { t } = useTranslation();

  const quickLinks = [
    { icon: Shield, labelKey: 'termsPage.quickLinks.privacyPolicy', anchor: '#section-0' },
    { icon: FileText, labelKey: 'termsPage.quickLinks.subscriptionTerms', anchor: '#section-1' },
    { icon: FileText, labelKey: 'termsPage.quickLinks.cancellationPolicy', anchor: '#section-2' },
    { icon: Shield, labelKey: 'termsPage.quickLinks.gdpr', anchor: '#section-3' },
  ];

  const handleAnchorClick = (anchor: string) => {
    const element = document.querySelector(anchor);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="page-shell">
      <Header />

      <section className="section-shell">
        <div className="section-container">
          <div className="surface-card-soft p-8 md:p-12">
            <SectionHeading
              align="center"
              eyebrow={t('termsPage.heroEyebrow')}
              title={t('terms.title')}
              subtitle={`${t('terms.lastUpdated')}: ${t('termsPage.lastUpdatedDate')}`}
            />
          </div>
        </div>
      </section>

      <section className="section-shell pt-0">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <div className="surface-card p-5">
                <h3 className="mb-4 text-lg font-bold text-ink">{t('terms.tableOfContents')}</h3>
                <nav className="space-y-2">
                  {termsContent.map((section, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnchorClick(`#section-${index}`)}
                      className="block w-full py-1 text-left text-sm text-slate-600 transition-colors hover:text-primary"
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>

                <div className="mt-6 rounded-2xl border border-primary/10 bg-primary/5 p-4">
                  <h4 className="mb-3 text-sm font-semibold text-ink">{t('terms.quickLinks')}</h4>
                  <div className="space-y-2">
                    {quickLinks.map((link, index) => {
                      const Icon = link.icon;
                      return (
                        <button
                          key={index}
                          onClick={() => handleAnchorClick(link.anchor)}
                          className="flex w-full items-center gap-2 text-left text-sm text-slate-600 transition-colors hover:text-primary"
                        >
                          <Icon className="h-4 w-4" />
                          {t(link.labelKey)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-3">
              <div className="surface-card mb-8 p-4 lg:hidden">
                <h3 className="mb-3 font-semibold text-ink">{t('terms.quickNavigation')}</h3>
                <div className="flex flex-wrap gap-2">
                  {quickLinks.map((link, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnchorClick(link.anchor)}
                      className="rounded-full border border-primary/15 bg-white px-3 py-1.5 text-sm text-slate-700 transition-colors hover:border-primary hover:text-primary"
                    >
                      {t(link.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="surface-card-soft mb-8 p-6">
                <h2 className="mb-2 text-xl font-bold text-ink">{t('terms.welcomeTitle')}</h2>
                <p className="text-slate-700">
                  {t('termsPage.welcomeIntro')}
                </p>
              </div>

              <div className="space-y-3">
                {termsContent.map((section, index) => (
                  <div key={index} id={`section-${index}`} className="scroll-mt-24">
                    <CollapsibleSection title={section.title} defaultOpen={index === 0}>
                      {/* XSS: section.content must be from trusted source only (e.g. static/i18n). Do not use user or CMS input without sanitization. */}
                      <div 
                        dangerouslySetInnerHTML={{ __html: section.content }} 
                        className="prose prose-gray max-w-none"
                      />
                    </CollapsibleSection>
                  </div>
                ))}
              </div>

              <div className="surface-card mt-12 p-6">
                <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-ink">
                  <Mail className="h-5 w-5 text-primary" />
                  {t('termsPage.questionsTitle')}
                </h3>
                <p className="mb-6 text-slate-600">
                  {t('termsPage.questionsDescription')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <a
                    href="mailto:legal@inburgertest.nl"
                    className="group flex items-center gap-2 rounded-2xl border border-primary/10 bg-white p-4 transition-all hover:border-primary"
                  >
                    <FileText className="h-5 w-5 text-slate-400 group-hover:text-primary" />
                    <div>
                      <div className="text-sm font-medium text-ink">{t('terms.legal')}</div>
                      <div className="text-xs text-primary">legal@inburgertest.nl</div>
                    </div>
                    <ExternalLink className="ml-auto h-4 w-4 text-slate-300" />
                  </a>
                  <a
                    href="mailto:privacy@inburgertest.nl"
                    className="group flex items-center gap-2 rounded-2xl border border-primary/10 bg-white p-4 transition-all hover:border-primary"
                  >
                    <Shield className="h-5 w-5 text-slate-400 group-hover:text-primary" />
                    <div>
                      <div className="text-sm font-medium text-ink">{t('terms.privacy')}</div>
                      <div className="text-xs text-primary">privacy@inburgertest.nl</div>
                    </div>
                    <ExternalLink className="ml-auto h-4 w-4 text-slate-300" />
                  </a>
                  <Link
                    to="/contact"
                    className="group flex items-center gap-2 rounded-2xl border border-primary/10 bg-white p-4 transition-all hover:border-primary"
                  >
                    <Mail className="h-5 w-5 text-slate-400 group-hover:text-primary" />
                    <div>
                      <div className="text-sm font-medium text-ink">{t('terms.support')}</div>
                      <div className="text-xs text-primary">{t('terms.contactForm')}</div>
                    </div>
                    <ExternalLink className="ml-auto h-4 w-4 text-slate-300" />
                  </Link>
                </div>
              </div>

              <div className="mt-8 text-center text-sm text-slate-500">
                <p>
                  {t('termsPage.versionNote')}
                </p>
                <p className="mt-1">
                  {t('termsPage.version')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
